import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Swal from 'sweetalert2';
import { normalizarFecha, obtenerFechaLocal, formatearFechaLegible } from '../utils/dateUtils'; 

const RegistroJornales = ({ proyectoId, proyecto }) => {
  const [empleados, setEmpleados] = useState([]);
  const [jornales, setJornales] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [loading, setLoading] = useState(true);
 const [fechaSeleccionada, setFechaSeleccionada] = useState(obtenerFechaLocal());
  const [filtros, setFiltros] = useState({
    empleadoId: '',
    desde: '',
    hasta: '',
    estado: '' // 'todos', 'pagados', 'pendientes'
  });
  const [vistaActual, setVistaActual] = useState('asistencia'); // 'asistencia', 'historial'

  useEffect(() => {
    cargarEmpleados();
    cargarJornales();
    cargarCuentasBancarias();
  }, [proyectoId, filtros]);

  const cargarCuentasBancarias = async () => {
    try {
      const empresaId = localStorage.getItem('empresa_contable_id');
      const params = empresaId ? `?empresa_id=${empresaId}` : '';
      const res = await api.get(`/bancos/cuentas${params}`);
      const lista = res.data.cuentas || (Array.isArray(res.data) ? res.data : []);
      setCuentasBancarias(lista);
    } catch (error) {
      console.error('Error al cargar cuentas bancarias:', error);
    }
  };

  const cargarEmpleados = async () => {
    try {
      const response = await api.get(`/proyectos/${proyectoId}/empleados`);
      setEmpleados(response.data.filter(e => e.activo));
    } catch (error) {
      console.error('Error al cargar empleados:', error);
    }
  };

  const cargarJornales = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filtros.empleadoId) params.append('empleadoId', filtros.empleadoId);
      if (filtros.desde) params.append('desde', filtros.desde);
      if (filtros.hasta) params.append('hasta', filtros.hasta);

      const response = await api.get(`/proyectos/${proyectoId}/jornales?${params.toString()}`);
      
      let jornalesFiltrados = response.data;
      if (filtros.estado === 'pagados') {
        jornalesFiltrados = jornalesFiltrados.filter(j => j.pagado);
      } else if (filtros.estado === 'pendientes') {
        jornalesFiltrados = jornalesFiltrados.filter(j => !j.pagado && j.trabajo);
      }
      
      setJornales(jornalesFiltrados);
    } catch (error) {
      console.error('Error al cargar jornales:', error);
      Swal.fire('Error', 'No se pudieron cargar los jornales', 'error');
    } finally {
      setLoading(false);
    }
  };

  const registrarAsistencia = async (empleadoId, trabajo, motivoAusencia = '') => {
    try {
      await api.post(`/proyectos/${proyectoId}/jornales`, {
        EmpleadoProyectoId: empleadoId,
        fecha: fechaSeleccionada,
        trabajo,
        motivo_ausencia: !trabajo ? motivoAusencia : null,
        horas_trabajadas: trabajo ? 8 : 0
      });

      Swal.fire('✅ Registrado', 'Asistencia registrada correctamente', 'success');
      cargarJornales();
    } catch (error) {
      console.error('Error al registrar asistencia:', error);
      if (error.response?.data?.mensaje) {
        Swal.fire('Error', error.response.data.mensaje, 'error');
      } else {
        Swal.fire('Error', 'No se pudo registrar la asistencia', 'error');
      }
    }
  };

  const registrarAusencia = async (empleado) => {
    const { value: motivo } = await Swal.fire({
      title: `Registrar ausencia de ${empleado.nombre}`,
      input: 'select',
      inputOptions: {
        'enfermedad': 'Enfermedad',
        'permiso': 'Permiso',
        'vacaciones': 'Vacaciones',
        'falta': 'Falta',
        'otro': 'Otro'
      },
      inputPlaceholder: 'Selecciona un motivo',
      showCancelButton: true,
      confirmButtonText: 'Registrar',
      cancelButtonText: 'Cancelar',
      inputValidator: (value) => {
        if (!value) {
          return 'Debes seleccionar un motivo';
        }
      }
    });

    if (motivo) {
      await registrarAsistencia(empleado.id, false, motivo);
    }
  };

  const registrarPago = async (jornalId) => {
    const opcionesCuentas = cuentasBancarias
      .map(c => `<option value="${c.id}">${c.nombre} — ${c.banco}</option>`)
      .join('');
    const sinCuentas = cuentasBancarias.length === 0;

    const { value: formValues } = await Swal.fire({
      title: '¿Método de pago?',
      html: `
        <div style="text-align:left; margin-bottom:10px;">
          <label style="font-weight:600; display:block; margin-bottom:6px;">Método</label>
          <select id="swal-metodo" class="swal2-input" style="margin:0; width:100%;">
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia bancaria</option>
          </select>
        </div>
        <div id="swal-cuenta-wrap" style="text-align:left; display:none;">
          <label style="font-weight:600; display:block; margin-bottom:6px;">Cuenta bancaria</label>
          <select id="swal-cuenta" class="swal2-input" style="margin:0; width:100%;">
            ${opcionesCuentas}
          </select>
        </div>
        <div id="swal-sin-cuentas" style="display:none; background:#fef3c7; border:1px solid #f59e0b; border-radius:8px; padding:12px; text-align:left; font-size:13px; color:#92400e;">
          ⚠️ No tienes cuentas bancarias registradas.<br/>
          Ve a <strong>Banco / Tesorería</strong> para agregar una antes de usar transferencia.
        </div>
      `,
      didOpen: () => {
        document.getElementById('swal-metodo').addEventListener('change', e => {
          const esTransferencia = e.target.value === 'transferencia';
          document.getElementById('swal-cuenta-wrap').style.display =
            esTransferencia && !sinCuentas ? 'block' : 'none';
          document.getElementById('swal-sin-cuentas').style.display =
            esTransferencia && sinCuentas ? 'block' : 'none';
        });
      },
      preConfirm: () => {
        const metodo = document.getElementById('swal-metodo').value;
        if (metodo === 'transferencia' && sinCuentas) {
          Swal.showValidationMessage('Debes registrar una cuenta bancaria primero en Banco / Tesorería');
          return false;
        }
        return {
          metodo_pago: metodo,
          cuenta_bancaria_id: document.getElementById('swal-cuenta').value || null
        };
      },
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Confirmar pago',
      cancelButtonText: 'Cancelar'
    });

    if (formValues) {
      try {
        await api.put(`/jornales/${jornalId}/pagar`, formValues);
        Swal.fire('✅ Pagado', 'Pago registrado correctamente', 'success');
        cargarJornales();
      } catch (error) {
        console.error('Error al registrar pago:', error);
        Swal.fire('Error', 'No se pudo registrar el pago', 'error');
      }
    }
  };

  const pagarSeleccionados = async () => {
    const jornalesPendientes = jornales.filter(j => !j.pagado && j.trabajo);

    if (jornalesPendientes.length === 0) {
      Swal.fire('Info', 'No hay jornales pendientes de pago', 'info');
      return;
    }

    const totalAPagar = jornalesPendientes.reduce((sum, j) => sum + parseFloat(j.monto_pagado || 0), 0);
    const opcionesCuentas = cuentasBancarias
      .map(c => `<option value="${c.id}">${c.nombre} — ${c.banco}</option>`)
      .join('');
    const sinCuentas = cuentasBancarias.length === 0;

    const { value: formValues } = await Swal.fire({
      title: '¿Pagar todos los jornales pendientes?',
      html: `
        <p><strong>${jornalesPendientes.length}</strong> jornal(es) — <strong style="color:#10b981">${formatMoney(totalAPagar)}</strong></p>
        <div style="text-align:left; margin-bottom:10px;">
          <label style="font-weight:600; display:block; margin-bottom:6px;">Método de pago</label>
          <select id="swal-metodo" class="swal2-input" style="margin:0; width:100%;">
            <option value="efectivo">Efectivo</option>
            <option value="transferencia">Transferencia bancaria</option>
          </select>
        </div>
        <div id="swal-cuenta-wrap" style="text-align:left; display:none;">
          <label style="font-weight:600; display:block; margin-bottom:6px;">Cuenta bancaria</label>
          <select id="swal-cuenta" class="swal2-input" style="margin:0; width:100%;">
            ${opcionesCuentas}
          </select>
        </div>
        <div id="swal-sin-cuentas" style="display:none; background:#fef3c7; border:1px solid #f59e0b; border-radius:8px; padding:12px; text-align:left; font-size:13px; color:#92400e;">
          ⚠️ No tienes cuentas bancarias registradas.<br/>
          Ve a <strong>Banco / Tesorería</strong> para agregar una antes de usar transferencia.
        </div>
      `,
      didOpen: () => {
        document.getElementById('swal-metodo').addEventListener('change', e => {
          const esTransferencia = e.target.value === 'transferencia';
          document.getElementById('swal-cuenta-wrap').style.display =
            esTransferencia && !sinCuentas ? 'block' : 'none';
          document.getElementById('swal-sin-cuentas').style.display =
            esTransferencia && sinCuentas ? 'block' : 'none';
        });
      },
      preConfirm: () => {
        const metodo = document.getElementById('swal-metodo').value;
        if (metodo === 'transferencia' && sinCuentas) {
          Swal.showValidationMessage('Debes registrar una cuenta bancaria primero en Banco / Tesorería');
          return false;
        }
        return {
          metodo_pago: metodo,
          cuenta_bancaria_id: document.getElementById('swal-cuenta').value || null
        };
      },
      showCancelButton: true,
      confirmButtonColor: '#10b981',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Confirmar pago',
      cancelButtonText: 'Cancelar'
    });

    if (formValues) {
      try {
        await api.post(`/proyectos/${proyectoId}/jornales/pagar-masivo`, {
          jornales_ids: jornalesPendientes.map(j => j.id),
          ...formValues
        });
        Swal.fire('✅ Pagados', 'Todos los jornales han sido pagados', 'success');
        cargarJornales();
      } catch (error) {
        console.error('Error al pagar:', error);
        Swal.fire('Error', 'No se pudo completar el pago', 'error');
      }
    }
  };

  const eliminarJornal = async (jornalId) => {
    const result = await Swal.fire({
      title: '¿Eliminar jornal?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ef4444',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/jornales/${jornalId}`);
        Swal.fire('✅ Eliminado', 'Jornal eliminado correctamente', 'success');
        cargarJornales();
      } catch (error) {
        console.error('Error al eliminar:', error);
        Swal.fire('Error', error.response?.data?.mensaje || 'No se pudo eliminar el jornal', 'error');
      }
    }
  };

  const formatMoney = (valor) => {
    return `RD$ ${parseFloat(valor || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

 const formatFecha = (fecha) => {
  if (!fecha) return 'N/A';
  return formatearFechaLegible(fecha);
};

 const obtenerJornalDelDia = (empleadoId) => {
  return jornales.find(j => 
    j.EmpleadoProyectoId === empleadoId && 
    normalizarFecha(j.fecha) === normalizarFecha(fechaSeleccionada)
  );
};

  const calcularTotales = () => {
    const totalPagado = jornales
      .filter(j => j.pagado)
      .reduce((sum, j) => sum + parseFloat(j.monto_pagado || 0), 0);
    
    const totalPendiente = jornales
      .filter(j => !j.pagado && j.trabajo)
      .reduce((sum, j) => sum + parseFloat(j.monto_pagado || 0), 0);

    return { totalPagado, totalPendiente };
  };

  const totales = calcularTotales();

  return (
    <div style={styles.container}>
      {/* Tabs */}
      <div style={styles.tabs}>
        <button
          style={{
            ...styles.tab,
            ...(vistaActual === 'asistencia' ? styles.tabActive : {})
          }}
          onClick={() => setVistaActual('asistencia')}
        >
          ✅ Registrar Asistencia
        </button>
        <button
          style={{
            ...styles.tab,
            ...(vistaActual === 'historial' ? styles.tabActive : {})
          }}
          onClick={() => setVistaActual('historial')}
        >
          📋 Historial de Jornales
        </button>
      </div>

      {/* Resumen */}
      <div style={styles.resumen}>
        <div style={styles.resumenCard}>
          <div style={styles.resumenIcon}>💰</div>
          <div>
            <p style={styles.resumenLabel}>Total Pagado</p>
            <h3 style={styles.resumenValue}>{formatMoney(totales.totalPagado)}</h3>
          </div>
        </div>
        <div style={styles.resumenCard}>
          <div style={styles.resumenIcon}>⏳</div>
          <div>
            <p style={styles.resumenLabel}>Pendiente de Pago</p>
            <h3 style={{...styles.resumenValue, color: '#f59e0b'}}>
              {formatMoney(totales.totalPendiente)}
            </h3>
          </div>
        </div>
        <div style={styles.resumenCard}>
          <div style={styles.resumenIcon}>👥</div>
          <div>
            <p style={styles.resumenLabel}>Empleados Activos</p>
            <h3 style={styles.resumenValue}>{empleados.length}</h3>
          </div>
        </div>
      </div>

      {vistaActual === 'asistencia' && (
        <>
          {/* Selector de Fecha */}
          <div style={styles.selectorFecha}>
            <label style={styles.label}>Fecha de Asistencia:</label>
            <input
              type="date"
              value={fechaSeleccionada}
              onChange={(e) => setFechaSeleccionada(e.target.value)}
              style={styles.inputFecha}
            />
          </div>

          {/* Lista de Empleados para Registrar */}
          {loading ? (
            <div style={styles.loading}>
              <div style={styles.spinner}></div>
              <p>Cargando...</p>
            </div>
          ) : empleados.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>👷</div>
              <h3>No hay empleados activos</h3>
              <p>Registra empleados primero para poder marcar asistencia</p>
            </div>
          ) : (
            <div style={styles.listaEmpleados}>
              {empleados.map((empleado) => {
                const jornalDelDia = obtenerJornalDelDia(empleado.id);
                
                return (
                  <div key={empleado.id} style={styles.empleadoCard}>
                    <div style={styles.empleadoInfo}>
                      <div>
                        <h4 style={styles.empleadoNombre}>{empleado.nombre}</h4>
                        <p style={styles.empleadoCargo}>
                          {empleado.cargo} • {formatMoney(empleado.tarifa_dia)}/
                          {empleado.tipo_pago === 'por_dia' ? 'día' : 'hora'}
                        </p>
                      </div>
                      
                      {jornalDelDia ? (
                        <div style={styles.estadoAsistencia}>
                          {jornalDelDia.trabajo ? (
                            <>
                              <span style={styles.badgePresente}>✅ Presente</span>
                              <span style={styles.montoJornal}>
                                {formatMoney(jornalDelDia.monto_pagado)}
                              </span>
                              {!jornalDelDia.pagado && (
                                <button
                                  onClick={() => eliminarJornal(jornalDelDia.id)}
                                  style={styles.btnEliminar}
                                  title="Eliminar registro"
                                >
                                  🗑️
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              <span style={styles.badgeAusente}>
                                ❌ Ausente ({jornalDelDia.motivo_ausencia})
                              </span>
                              {!jornalDelDia.pagado && (
                                <button
                                  onClick={() => eliminarJornal(jornalDelDia.id)}
                                  style={styles.btnEliminar}
                                  title="Eliminar registro"
                                >
                                  🗑️
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      ) : (
                        <div style={styles.botonesAsistencia}>
                          <button
                            onClick={() => registrarAsistencia(empleado.id, true)}
                            style={styles.btnPresente}
                          >
                            ✅ Presente
                          </button>
                          <button
                            onClick={() => registrarAusencia(empleado)}
                            style={styles.btnAusente}
                          >
                            ❌ Ausente
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {vistaActual === 'historial' && (
        <>
          {/* Filtros */}
          <div style={styles.filtros}>
            <select
              value={filtros.empleadoId}
              onChange={(e) => setFiltros({...filtros, empleadoId: e.target.value})}
              style={styles.select}
            >
              <option value="">Todos los empleados</option>
              {empleados.map(e => (
                <option key={e.id} value={e.id}>{e.nombre}</option>
              ))}
            </select>

            <input
              type="date"
              value={filtros.desde}
              onChange={(e) => setFiltros({...filtros, desde: e.target.value})}
              style={styles.input}
              placeholder="Desde"
            />

            <input
              type="date"
              value={filtros.hasta}
              onChange={(e) => setFiltros({...filtros, hasta: e.target.value})}
              style={styles.input}
              placeholder="Hasta"
            />

            <select
              value={filtros.estado}
              onChange={(e) => setFiltros({...filtros, estado: e.target.value})}
              style={styles.select}
            >
              <option value="">Todos</option>
              <option value="pendientes">Pendientes</option>
              <option value="pagados">Pagados</option>
            </select>

            {(filtros.empleadoId || filtros.desde || filtros.hasta || filtros.estado) && (
              <button
                onClick={() => setFiltros({ empleadoId: '', desde: '', hasta: '', estado: '' })}
                style={styles.btnLimpiar}
              >
                Limpiar
              </button>
            )}

            {totales.totalPendiente > 0 && (
              <button
                onClick={pagarSeleccionados}
                style={styles.btnPagarTodo}
              >
                💰 Pagar Todo ({formatMoney(totales.totalPendiente)})
              </button>
            )}
          </div>

          {/* Historial */}
          {loading ? (
            <div style={styles.loading}>
              <div style={styles.spinner}></div>
              <p>Cargando historial...</p>
            </div>
          ) : jornales.length === 0 ? (
            <div style={styles.empty}>
              <div style={styles.emptyIcon}>📋</div>
              <h3>No hay jornales registrados</h3>
              <p>Los registros de asistencia aparecerán aquí</p>
            </div>
          ) : (
            <div style={styles.listaJornales}>
              {jornales.map((jornal) => (
                <div key={jornal.id} style={styles.jornalCard}>
                  <div style={styles.jornalHeader}>
                    <div>
                      <h4 style={styles.jornalEmpleado}>{jornal.empleado?.nombre}</h4>
                      <p style={styles.jornalFecha}>{formatFecha(jornal.fecha)}</p>
                    </div>
                    <div style={styles.jornalMonto}>
                      {formatMoney(jornal.monto_pagado)}
                    </div>
                  </div>

                  <div style={styles.jornalInfo}>
                    <div>
                      {jornal.trabajo ? (
                        <span style={styles.badgePresente}>✅ Trabajó</span>
                      ) : (
                        <span style={styles.badgeAusente}>
                          ❌ {jornal.motivo_ausencia}
                        </span>
                      )}
                    </div>
                    <div>
                      {jornal.pagado ? (
                        <span style={styles.badgePagado}>
                          💰 Pagado {jornal.fecha_pago && `(${formatFecha(jornal.fecha_pago)})`}
                        </span>
                      ) : jornal.trabajo ? (
                        <button
                          onClick={() => registrarPago(jornal.id)}
                          style={styles.btnPagar}
                        >
                          💰 Registrar Pago
                        </button>
                      ) : (
                        <span style={styles.badgeSinPago}>Sin pago</span>
                      )}
                    </div>
                  </div>

                  {jornal.observaciones && (
                    <p style={styles.observaciones}>📝 {jornal.observaciones}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: '20px'
  },
  tabs: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '2px solid #e2e8f0'
  },
  tab: {
    padding: '12px 24px',
    background: 'none',
    border: 'none',
    borderBottom: '3px solid transparent',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: '600',
    color: '#64748b',
    transition: 'all 0.2s'
  },
  tabActive: {
    color: '#3b82f6',
    borderBottom: '3px solid #3b82f6'
  },
  resumen: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '15px',
    marginBottom: '20px'
  },
  resumenCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  resumenIcon: {
    fontSize: '36px'
  },
  resumenLabel: {
    fontSize: '13px',
    color: '#64748b',
    margin: '0 0 5px 0',
    fontWeight: '600'
  },
  resumenValue: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#1e293b',
    margin: 0
  },
  selectorFecha: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#475569'
  },
  inputFecha: {
    padding: '10px 12px',
    fontSize: '16px',
    border: '2px solid #cbd5e1',
    borderRadius: '8px',
    fontWeight: '600'
  },
  filtros: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    flexWrap: 'wrap'
  },
  select: {
    padding: '10px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px',
    background: 'white'
  },
  input: {
    padding: '10px 12px',
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    fontSize: '14px'
  },
  btnLimpiar: {
    padding: '10px 16px',
    background: '#f1f5f9',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    color: '#64748b',
    cursor: 'pointer'
  },
  btnPagarTodo: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    marginLeft: 'auto',
    boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
  },
  loading: {
    textAlign: 'center',
    padding: '40px'
  },
  spinner: {
    border: '4px solid #f3f4f6',
    borderTop: '4px solid #3b82f6',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 15px'
  },
  empty: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px',
    border: '2px dashed #cbd5e1'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '15px'
  },
  listaEmpleados: {
    display: 'grid',
    gap: '15px'
  },
  empleadoCard: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  empleadoInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '15px'
  },
  empleadoNombre: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  empleadoCargo: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0
  },
  estadoAsistencia: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  badgePresente: {
    padding: '6px 12px',
    background: '#dcfce7',
    color: '#166534',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600'
  },
  badgeAusente: {
    padding: '6px 12px',
    background: '#fee2e2',
    color: '#991b1b',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600'
  },
  badgePagado: {
    padding: '6px 12px',
    background: '#dbeafe',
    color: '#1e40af',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600'
  },
  badgeSinPago: {
    padding: '6px 12px',
    background: '#f3f4f6',
    color: '#64748b',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600'
  },
  montoJornal: {
    fontSize: '16px',
    fontWeight: '700',
    color: '#10b981'
  },
  botonesAsistencia: {
    display: 'flex',
    gap: '10px'
  },
  btnPresente: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnAusente: {
    padding: '10px 20px',
    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  btnEliminar: {
    padding: '6px 12px',
    background: '#fef2f2',
    color: '#dc2626',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px'
  },
  listaJornales: {
    display: 'grid',
    gap: '15px'
  },
  jornalCard: {
    background: 'white',
    padding: '20px',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid #e2e8f0'
  },
  jornalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px'
  },
  jornalEmpleado: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#1e293b',
    margin: '0 0 4px 0'
  },
  jornalFecha: {
    fontSize: '13px',
    color: '#64748b',
    margin: 0
  },
  jornalMonto: {
    fontSize: '20px',
    fontWeight: '700',
    color: '#10b981'
  },
  jornalInfo: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  btnPagar: {
    padding: '8px 16px',
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  observaciones: {
    marginTop: '10px',
    padding: '10px',
    background: '#f8fafc',
    borderRadius: '6px',
    fontSize: '13px',
    color: '#475569'
  }
};

export default RegistroJornales;